import { ActionSteps } from "src/components/ContinueDeal/CreditApp/CreditApp/enums";
import {
    ContinueCreditAppAction,
    GoBackCreditAppAction,
    ResetCreditAppAction,
    SetCosignerQuestion,
    SetCreditApplicationId,
    setCreditApplicationPageToDecision,
    SkipCreditApplication,
} from "src/store/actions/creditApp/base";
import { getDealPersonSuccess } from "src/store/actions/deal";
import {
    CONTINUE_CREDIT_APP,
    GO_BACK_CREDIT_APP,
    RESET_CREDIT_APP,
    SET_COSIGNER_QUESTION,
    SET_CREDIT_APPLICATION_ID,
    SKIP_CREDIT_APP,
} from "src/store/constants/creditApp";
import { GetAccountResponseSuccessAction } from "src/store/dataService/actions/account";
import { CreateDealResponseAction } from "src/store/dataService/actions/deal";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { AccountResponses } from "src/store/dataService/types";
import State, { Base as BaseState } from "Types/creditApp";

interface Step {
    id: number;
    name: string;
    group: number;
}

const groups = {
    0: { name: "About Me" },
    1: { name: "My Employer" },
    2: { name: "My Cosigner" },
    3: { name: "Terms and Conditions" },
    4: { name: "Decision" },
};

const steps: Step[] = [
    { id: 0, name: "Contact", group: 0 },
    { id: 1, name: "Identification", group: 0 },
    { id: 2, name: "Residence", group: 0 },
    { id: 3, name: "Bankruptcy", group: 0 },

    { id: 4, name: "Employment", group: 1 },
    { id: 5, name: "Income", group: 1 },

    { id: 6, name: "My Cosigner", group: 2 },
    { id: 7, name: "Contact", group: 2 },
    { id: 8, name: "Identification", group: 2 },
    { id: 9, name: "Residence", group: 2 },
    { id: 10, name: "Bankruptcy", group: 2 },

    { id: 11, name: "Employment", group: 2 },
    { id: 12, name: "Income", group: 2 },

    {
        group: 3,
        id: 13,
        name: "Terms and Conditions",
    },

    { id: 14, name: "Review", group: 4 },
    { id: 15, name: "Decision", group: 4 },
];

const tabGroups = [{ groups: [0, 1, 2] }, { groups: [3] }, { groups: [4] }];

const breadcrumbs = [
    { steps: [0, 1, 2, 3], group: 0 },
    { steps: [4, 5], group: 1 },
    { steps: [7, 8, 9, 10], group: 2 },
    { steps: [11, 12], group: 2 },
    { steps: [13], group: 3 },
    { steps: [14, 15], group: 4 },
];

const DEFAULT_STEP = 0;
const defaultStepData = getStepData(DEFAULT_STEP);

export const initialState: BaseState = {
    applicationId: undefined,
    done: false,
    hasCosigner: false,
    status: undefined,
    submission: undefined,
    ...defaultStepData,
};

type Actions =
    | ActionType<typeof setCreditApplicationPageToDecision>
    | ContinueCreditAppAction
    | GoBackCreditAppAction
    | SkipCreditApplication
    | SetCosignerQuestion
    | SetCreditApplicationId
    | CreateDealResponseAction
    | ResetCreditAppAction
    | GetAccountResponseSuccessAction
    | ActionType<typeof getDealPersonSuccess>;

function creditApp(state: BaseState = initialState, action: Actions, creditAppState?: State): BaseState {
    const { type } = action;
    switch (type) {
        case CONTINUE_CREDIT_APP: {
            const nextStepId = getNextStep(state, creditAppState);

            if (!nextStepId) {
                return {
                    ...state,
                    done: true,
                    status: "submitted",
                };
            }

            const stepData = getStepData(nextStepId);

            return {
                ...state,
                ...stepData,
            };
        }

        case GO_BACK_CREDIT_APP: {
            const previousStepId = getPreviousStep(state);

            const stepData = getStepData(previousStepId);

            return {
                ...state,
                ...stepData,
            };
        }

        case RESET_CREDIT_APP: {
            return {
                ...initialState,
            };
        }

        case SKIP_CREDIT_APP: {
            return {
                ...state,
                done: false,
                status: "skipped",
            };
        }

        case SET_COSIGNER_QUESTION: {
            const { data } = action as SetCosignerQuestion;
            return {
                ...state,
                hasCosigner: data.hasCosigner,
            };
        }

        case SET_CREDIT_APPLICATION_ID: {
            const { data } = action as SetCreditApplicationId;
            return {
                ...state,
                applicationId: data.application_id,
                status: "submitted",
                submission: {
                    vin: data.vin,
                    time: new Date().getTime(),
                },
            };
        }

        case getDealPersonSuccess.type: {
            const { payload } = action;
            return {
                ...state,
                applicationId: payload.person.credit_application_id,
                status:
                    state.status !== "disabled"
                        ? payload.person.credit_application_id
                            ? "submitted"
                            : undefined
                        : state.status,
            };
        }

        case AccountResponses.GetSuccess: {
            const { data } = action as GetAccountResponseSuccessAction;
            return {
                ...state,
                status:
                    !(data.integration_settings && data.integration_settings.DI_Credit) ||
                    !(data.integration_settings && data.integration_settings.DI_Credit.dealer_id)
                        ? "disabled"
                        : undefined,
            };
        }

        case setCreditApplicationPageToDecision.type:
            const stepData = getStepData(15);

            return {
                ...state,
                ...stepData,
            };

        default:
            return state;
    }
}

export default creditApp;

export function findStep(id: number) {
    return steps.find((f) => f.id === id);
}

function getNextStep({ currentStep, hasCosigner }: BaseState, creditAppState?: State) {
    if (
        creditAppState?.settings?.instant_decisioning || creditAppState?.settings?.mock_mode
            ? currentStep === ActionSteps.FINAL_STEP
            : currentStep === ActionSteps.TERMS_STEP
    ) {
        return null;
    }

    if (currentStep === ActionSteps.PRIMARY_END) {
        return hasCosigner ? currentStep + 1 : ActionSteps.TERMS_STEP;
    }

    return currentStep + 1;
}

function getPreviousStep({ currentStep, hasCosigner }: BaseState) {
    if (currentStep === ActionSteps.PRIMARY_START) {
        return 0;
    }

    if (currentStep === ActionSteps.TERMS_STEP && !hasCosigner) {
        return ActionSteps.PRIMARY_END;
    }

    return currentStep - 1;
}

function getTabs(step: Step) {
    const x = tabGroups.find((f) => f.groups.some((s) => s === step.group));

    return x
        ? {
              current: x.groups.indexOf(step.group),
              names: x.groups.map<string>((m) => groups[m].name),
          }
        : { names: [] };
}

function getBreadcrumb(step: Step) {
    const bc = breadcrumbs.find((f) => f.steps.some((s) => s === step.id));

    return bc
        ? {
              current: bc.steps.indexOf(step.id),
              names: bc.steps.map((m) => (findStep(m) as Step).name),
          }
        : { names: [] };
}

export function getStepData(stepId: number) {
    const step = findStep(stepId) as Step;
    const currentTabs = getTabs(step);
    const bc = getBreadcrumb(step);

    return {
        breadcrumb: bc,
        currentStep: stepId,
        tabs: currentTabs,
    };
}
